import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getGaps } from "./api"

const PAGE_SIZE = 100

const formatFecha = iso => {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    return d.toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

const TabGaps = ({ nitemisor, prefijo, fechaDesde, fechaHasta }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(0)

  const cargar = useCallback(
    async pageIdx => {
      setLoading(true)
      setError("")
      try {
        const { data } = await getGaps({
          nitemisor,
          prefijo,
          limit: PAGE_SIZE,
          offset: pageIdx * PAGE_SIZE,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
        })
        setData(data)
      } catch (err) {
        setError(err.response?.data?.message || "No se pudo cargar la lista de gaps.")
      } finally {
        setLoading(false)
      }
    },
    [nitemisor, prefijo, fechaDesde, fechaHasta],
  )

  useEffect(() => {
    setPage(0)
    cargar(0)
  }, [cargar])

  const total = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const gaps = data?.gaps || []

  const irPagina = nuevaPage => {
    if (nuevaPage < 0 || nuevaPage >= totalPages) return
    setPage(nuevaPage)
    cargar(nuevaPage)
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "#dc2626", background: "#fef2f2", borderRadius: 10 }}>
        <i className="mdi mdi-alert-circle me-2" />
        {error}
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span style={{ fontSize: 14, color: "#64748b" }}>Total de folios faltantes</span>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#dc2626" }}>
            {total.toLocaleString("es-CO")}
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Página {page + 1} de {totalPages}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando…</div>
        </div>
      ) : (
        <>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <div style={{ maxHeight: 480, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                  <tr>
                    {["#", "Folio", "Fecha estimada"].map(h => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 14px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          borderBottom: "1px solid #e2e8f0",
                          textAlign: h === "Folio" ? "right" : "left",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gaps.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                        Sin gaps en este rango.
                      </td>
                    </tr>
                  )}
                  {gaps.map((g, i) => (
                    <tr key={`${g.folio}-${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 13 }}>
                        {page * PAGE_SIZE + i + 1}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          textAlign: "right",
                          fontWeight: 600,
                          color: "#dc2626",
                          fontFamily: "ui-monospace, SFMono-Regular, monospace",
                        }}
                      >
                        {g.folio}
                      </td>
                      <td style={{ padding: "10px 14px", color: "#475569", fontSize: 13 }}>
                        {formatFecha(g.fecha_estimada)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => irPagina(page - 1)}
                disabled={page === 0}
              >
                <i className="mdi mdi-chevron-left" /> Anterior
              </button>
              <span style={{ fontSize: 13, color: "#64748b" }}>
                Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total.toLocaleString("es-CO")}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => irPagina(page + 1)}
                disabled={page + 1 >= totalPages}
              >
                Siguiente <i className="mdi mdi-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

TabGaps.propTypes = {
  nitemisor: PropTypes.string.isRequired,
  prefijo: PropTypes.string.isRequired,
  fechaDesde: PropTypes.string,
  fechaHasta: PropTypes.string,
}

export default TabGaps
